import { useState, useEffect, type FormEvent } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useCurrentOrg } from "@/hooks/use-current-org";
import { authClient } from "@/lib/auth-client";
import { useMembers } from "@/hooks/use-members";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Loader2, UserPlus, X, Mail, RotateCw, Settings, UserMinus } from "lucide-react";

export function MembersPage() {
  usePageTitle("Members");
  const { org } = useCurrentOrg();
  const { data: session } = authClient.useSession();
  const { members: memberList, isPending: membersPending } = useMembers();
  const queryClient = useQueryClient();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "manager" | "admin">("member");
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [invitations, setInvitations] = useState<any[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Role edit modal state
  const [editMember, setEditMember] = useState<{ id: string; name: string; role: string } | null>(null);
  const [editRole, setEditRole] = useState<"member" | "manager" | "admin">("member");
  const [editLoading, setEditLoading] = useState(false);

  // Remove member confirmation state
  const [removeMember, setRemoveMember] = useState<{ id: string; name: string } | null>(null);

  const pendingInvitations = invitations.filter((i) => i.status === "pending");
  const currentUserId = session?.user?.id;

  useEffect(() => {
    if (!org) return;
    authClient.organization.listInvitations({
      query: { organizationId: org.id },
    }).then(({ data }) => {
      if (data) setInvitations(data);
      setInvitationsLoading(false);
    });
  }, [org?.id]);

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    setLoading(true);

    const { error: inviteError } = await authClient.organization.inviteMember({
      email,
      role: role as "member" | "admin",
      organizationId: org!.id,
    });

    if (inviteError) {
      toast.error(inviteError.message ?? "Failed to send invitation");
      setLoading(false);
      return;
    }

    toast.success(`Invitation sent to ${email}`);
    setEmail("");
    setRole("member");
    setLoading(false);
    setInviteOpen(false);

    const { data } = await authClient.organization.listInvitations({
      query: { organizationId: org!.id },
    });
    if (data) setInvitations(data);
  }

  async function handleCancelInvitation(invitationId: string) {
    const { error } = await authClient.organization.cancelInvitation({
      invitationId,
    });

    if (error) {
      toast.error(error.message ?? "Failed to cancel invitation");
      return;
    }

    toast.success("Invitation canceled");
    setInvitations((prev) => prev.filter((i) => i.id !== invitationId));
  }

  async function handleResendInvitation(invitationId: string, invitationEmail: string, invitationRole: string) {
    setResendingId(invitationId);

    const { error } = await authClient.organization.inviteMember({
      email: invitationEmail,
      role: invitationRole as "member" | "admin",
      organizationId: org!.id,
      resend: true,
    });

    setResendingId(null);

    if (error) {
      toast.error(error.message ?? "Failed to resend invitation");
      return;
    }

    toast.success(`Invitation resent to ${invitationEmail}`);
  }

  async function handleUpdateRole() {
    if (!editMember) return;
    setEditLoading(true);

    const { error } = await authClient.organization.updateMemberRole({
      memberId: editMember.id,
      role: editRole,
      organizationId: org!.id,
    });

    setEditLoading(false);

    if (error) {
      toast.error(error.message ?? "Failed to update role");
      return;
    }

    toast.success(`${editMember.name} is now ${editRole}`);
    setEditMember(null);
    await queryClient.invalidateQueries({ queryKey: ["members"] });
  }

  async function handleRemoveMember() {
    if (!removeMember) return;

    const { error } = await authClient.organization.removeMember({
      memberIdOrEmail: removeMember.id,
      organizationId: org!.id,
    });

    if (error) {
      toast.error(error.message ?? "Failed to remove member");
      setRemoveMember(null);
      return;
    }

    toast.success(`${removeMember.name} removed`);
    setRemoveMember(null);
    await queryClient.invalidateQueries({ queryKey: ["members"] });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Members</h1>
          <p className="text-muted-foreground">
            Manage your organization members
          </p>
        </div>

        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button>
              <UserPlus className="mr-2 h-4 w-4" />
              Invite Member
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite a member</DialogTitle>
              <DialogDescription>
                Send an invitation email to add someone to your organization
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="colleague@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select value={role} onValueChange={(v) => setRole(v as "member" | "manager" | "admin")}>
                  <SelectTrigger id="invite-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Send Invitation
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">
            Members ({memberList.length})
          </TabsTrigger>
          <TabsTrigger value="invitations">
            Pending ({pendingInvitations.length})
          </TabsTrigger>
        </TabsList>

        {/* Members tab */}
        <TabsContent value="members" className="mt-4">
          {membersPending ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : memberList.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No members yet
            </p>
          ) : (
            <div className="space-y-2">
              {memberList.map((member) => {
                const isSelf = member.userId === currentUserId;
                const isOwner = member.role === "owner";

                return (
                  <div
                    key={member.id}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="text-xs">
                          {member.user.name?.[0]?.toUpperCase() ?? member.user.email?.[0]?.toUpperCase() ?? "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium">
                          {member.user.name}
                          {isSelf && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}
                        </p>
                        <p className="text-xs text-muted-foreground">{member.user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-md bg-secondary px-2 py-1 text-xs font-medium">
                        {member.role}
                      </span>
                      {!isSelf && !isOwner && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Change role"
                            onClick={() => {
                              setEditMember({ id: member.id, name: member.user.name ?? member.user.email, role: member.role });
                              setEditRole(member.role as "member" | "manager" | "admin");
                            }}
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Remove member"
                            onClick={() => setRemoveMember({ id: member.id, name: member.user.name ?? member.user.email })}
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Invitations tab */}
        <TabsContent value="invitations" className="mt-4">
          {invitationsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : pendingInvitations.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No pending invitations
            </p>
          ) : (
            <div className="space-y-2">
              {pendingInvitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex items-center justify-between rounded-lg border border-dashed p-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{invitation.email}</p>
                      <p className="text-xs text-muted-foreground">
                        Invited as {invitation.role}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResendInvitation(invitation.id, invitation.email, invitation.role)}
                      disabled={resendingId === invitation.id}
                      aria-label="Resend invitation"
                    >
                      {resendingId === invitation.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RotateCw className="h-4 w-4" />
                      )}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="sm" aria-label="Cancel invitation">
                          <X className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cancel invitation?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will cancel the invitation to {invitation.email}. They will no longer be able to join.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Keep</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleCancelInvitation(invitation.id)}>
                            Cancel invitation
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Change role modal */}
      <Dialog open={!!editMember} onOpenChange={(open) => !open && setEditMember(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change role</DialogTitle>
            <DialogDescription>
              Update the role for {editMember?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editRole} onValueChange={(v) => setEditRole(v as "member" | "manager" | "admin")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditMember(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateRole} disabled={editLoading || editRole === editMember?.role}>
                {editLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove member confirmation */}
      <AlertDialog open={!!removeMember} onOpenChange={(open) => !open && setRemoveMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {removeMember?.name} from the organization. They will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
